/**
 * Header rules, pure. R13: box 1's "data as of" is Home's rule (the newest
 * channel data-through) when the channels were read, else the newest facts
 * date. R16: box 6 is judged in the page currency, which only the facts know.
 * rules-04 B2: box 8's coverage carries the page currency when the facts hold
 * spend in more than one, and waits while they load.
 *
 * @module services/overview/overviewHeader
 */
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import type { CoverageState } from '@/hooks/useLookerCoverage';
import type { CurrencyChoice, FactPair, ReadState } from './overviewTypes';
import type { OverviewView } from './overviewView';

export const latestDataDateOf = (freshness: ChannelFreshnessState, view: OverviewView | null): string | null => {
  if (freshness.status === 'ready') {
    return freshness.channels.flatMap((c) => (c.dataThrough ? [c.dataThrough] : [])).sort().pop() ?? null;
  }
  return view?.latestFactDate ?? null;
};

/** undefined = wait (facts loading); 'failed' = box 6 fails with the facts; else the page currency (null = none known). */
export const pacingCurrencyOf = (facts: ReadState<FactPair>, view: OverviewView | null): string | null | undefined | 'failed' => {
  if (facts.status === 'failed') return 'failed';
  if (facts.status !== 'ready') return undefined;
  return view?.money?.currency ?? null;
};

/**
 * Box 8 (rules-04 B2, FR-X-02): coverage reads every currency of the workspace. With spend in more than one currency
 * D1 marks the coverage with its page currency, exactly when /looker's server forces one (useLookerCoverage), and
 * coverageLinesOf says it covers every currency. The naming share counts campaigns, so it stands (rules-04 E2).
 * The page currency is not known while the facts load, so a ready coverage waits with them.
 */
export const coverageInCurrencyOf = (
  coverage: CoverageState,
  money: CurrencyChoice | null,
  factsStatus: ReadState<unknown>['status'],
): CoverageState => {
  if (coverage.status !== 'ready') return coverage;
  if (factsStatus === 'loading') return { status: 'loading' };
  // The facts failed: unchanged. A THB-only workspace keeps its %, and the facts box already says it failed.
  if (factsStatus === 'failed') return coverage;
  return money !== null && money.currency !== null && money.currencies.length > 1
    ? { ...coverage, currency: money.currency }
    : coverage;
};
